import { Button, Modal, Text, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { InterpreterFrom } from 'xstate';
import { useActor } from '@xstate/react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useEffect } from 'react';
import { closedModal, loadedModal } from '@hotjar/tracking';
import { useShortLinkWidgetHiddenState } from '@hotjar/webapp/features/ShortLinkWidget';
import type { Customer } from '~/subscription-management/api/common/model/customer';
import type { FormInputs } from '~/subscription-management/components/invoice-details/form/InvoiceDetailsForm';
import {
  FORM_ID,
  InvoiceDetailsForm,
} from '~/subscription-management/components/invoice-details/form/InvoiceDetailsForm';
import type { invoicesPageMachine } from '../../invoices.machine';
import { FORM_NAME } from '~/subscription-management/utils/tracking';
import { shouldMigrateAddress as shouldMigrateAddressFn } from '~/subscription-management/utils/migrated-customers.utils';
import { UnstructuredAddressInfo } from '~/subscription-management/components';

interface Props {
  customer: Pick<Customer, 'companyName' | 'billingAddress' | 'taxNumber'>;
  accountId: number;
  invoiceMachineService: InterpreterFrom<typeof invoicesPageMachine>;
  onCancel: () => void;
  onSubmit: () => void;
}

const S = {
  ModalBody: styled(Modal.Body)`
    padding: 0;
    flex-grow: 1;
  `,
  Info: styled(Text)`
    padding: ${tokens.spacer.x3};
  `,
  FormContainer: styled.div`
    background-color: ${tokens.ALIAS_COLORS.bg_info};
    padding: ${tokens.spacer.x3} ${tokens.spacer.x3} ${tokens.spacer.x6} ${tokens.spacer.x3};
    min-height: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: ${tokens.spacer.x3};
  `,
  UnstructuredAddressInfo: styled(UnstructuredAddressInfo)`
    position: sticky;
    top: ${tokens.spacer.x3};
  `,
};

const MODAL_NAME = 'Update invoice details';

export function InvoiceDetailsModal({
  customer: { companyName, taxNumber = '', billingAddress },
  accountId,
  invoiceMachineService,
  onCancel,
  onSubmit,
}: Props): JSX.Element {
  const [state, send] = useActor(invoiceMachineService);
  const { t } = useTranslationWithPrefix('checkout:invoices.invoice-details-modal');
  const { setIsHidden } = useShortLinkWidgetHiddenState();

  const isUpdating = state.matches('details.viewInvoiceDetails.address.update');
  const isEditing = state.matches('details.viewInvoiceDetails.address.editing');
  const shouldMigrateAddress = shouldMigrateAddressFn(billingAddress);
  if (shouldMigrateAddress) {
    // As a workaround we need to hide the ShorLinkWidget when the modal is fullscreen.
    // More context: https://hotjar.slack.com/archives/CDRS7AM5M/p1675172945115059
    setIsHidden(true);
  }

  const handleSubmit = async (formValue: FormInputs) => {
    const { addressLine1, addressLine2, city, zipCode, country, county } = formValue;

    send({
      type: 'UPDATE_ADDRESS',
      newCustomer: {
        hotjarAccountId: accountId,
        companyName: formValue.companyName,
        billingAddress: {
          addressLine1,
          addressLine2,
          city,
          zipCode,
          country,
          county,
          state: formValue.state,
          firstName: billingAddress?.firstName ?? '',
          lastName: billingAddress?.lastName ?? '',
          emailAddress: billingAddress?.emailAddress ?? '',
        },
        taxNumber: formValue.taxNumber,
      },
    });
    onSubmit();
  };

  const handleCancel = () => {
    closedModal(MODAL_NAME, { account_id: accountId });
    onCancel();
  };

  useEffect(() => {
    if (isEditing) {
      loadedModal(MODAL_NAME, { account_id: accountId });
    }
  }, [isEditing, accountId]);

  return (
    <Modal
      contentLabel={t('title')}
      isOpen={isEditing || isUpdating}
      onClose={handleCancel}
      size={shouldMigrateAddress ? 'fullscreen' : 'l'}
      appElementSelector="#app-container"
    >
      <Modal.Header title={t('title')} />

      <S.ModalBody>
        {!shouldMigrateAddress && <S.Info as="p">{t('info')}</S.Info>}

        <S.FormContainer>
          <InvoiceDetailsForm
            defaultValues={{ ...billingAddress, companyName, taxNumber }}
            onSubmit={handleSubmit}
            formId={FORM_ID}
            accountId={accountId}
            error={state.context.hasAddressError === true ? t('error-generic') : undefined}
            formName={FORM_NAME.updateInvoiceDetails}
          />
          {shouldMigrateAddress && (
            <S.UnstructuredAddressInfo
              unstructuredAddress={billingAddress?.unstructuredAddress}
              taxNumber={taxNumber}
              companyName={companyName}
            />
          )}
        </S.FormContainer>
      </S.ModalBody>

      <Modal.Footer>
        <Button kind="primary" type="submit" form={FORM_ID} isLoading={isUpdating}>
          {t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
